import { useState } from "react";
import { CloseAuditSaveModel } from "./CloseAuditDialog.types";
import { useValidator } from "@common/hooks/validation";
import { useTranslation } from "../../../localization/useTranslation";
import { put, uploadFile } from "@common/api/apiShared.ts";
import { useUploadProgress } from "@common/components/uploadProgress/useUploadProgress";
import { createPlaceholderFile, getFileToUpload } from "@common/documentUploadFile";
import { ICloseAuditDialog } from "./CloseAuditDialog";
import { DocumentUploadViewModel, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { isNullOrWhitespace } from "@common/stringOperations";

export default function useCloseAuditDialgHooks(props: ICloseAuditDialog) {
    const { translateString } = useTranslation();
    const [saveModel, setSaveModel] = useState<CloseAuditSaveModel>({ closedReason: undefined, sendNotificationToContact: false });
    const [documentUploadModel, setDocumentUploadModel] = useState<DocumentUploadViewModel>(new DocumentUploadViewModel([createPlaceholderFile()], undefined));
    const snackbar = useDotLegalSnackbar();
    const { uploadPercentage, trackUploadProgress } = useUploadProgress();

    const validation = useValidator<Partial<CloseAuditSaveModel>>((validators) => {
        return {
            closedReason: validators.validateNotEmpty((item) => item.closedReason, translateString("reason")),
        };
    }, []);

    const onStatusChange = (status: number) => {
        const temp = { ...saveModel };
        temp.closedReason = status;
        setSaveModel(temp);
    };

    const onSendNotificationToContactChange = (sendNotificationToContact: boolean) => {
        setSaveModel({ ...saveModel, sendNotificationToContact });
    };

    const closeAuditMutation = useDotLegalMutation(closeAuditAPI, {
        onSuccess: () => {
            snackbar.show(translateString("auditClosed"));
            props.onAuditClosed();
        },
    });

    const onCloseClick = () => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        closeAuditMutation.mutateAsync();
    };

    return {
        saveModel,
        onStatusChange,
        onSendNotificationToContactChange,
        validation,
        onCloseClick,
        isSaving: closeAuditMutation.isLoading,
        isUploading: closeAuditMutation.isLoading && getFileToUpload(documentUploadModel.files) !== undefined,
        uploadPercentage,
        documentUploadModel,
        setDocumentUploadModel,
    };

    async function closeAuditAPI() {
        const file = getFileToUpload(documentUploadModel.files);
        const uploadId = file ? await uploadFile(file, trackUploadProgress()) : undefined;
        return put(`/legalEntityAudit/${props.auditId}/close`, mapToSaveModel(uploadId));
    }

    function mapToSaveModel(uploadId: string | undefined): CloseAuditSaveModel {
        const hasFiles = documentUploadModel.files && documentUploadModel.files.length;
        return {
            closedReason: saveModel.closedReason,
            sendNotificationToContact: saveModel.sendNotificationToContact,
            document:
                (hasFiles > 0 && documentUploadModel.files.some((x) => x.file.size > 0)) || !isNullOrWhitespace(documentUploadModel.link)
                    ? {
                          fileName: hasFiles ? documentUploadModel.files[0].fileName : "",
                          link: documentUploadModel.link,
                          uploadId,
                      }
                    : undefined,
        };
    }
}
